// components/booking/BookingDetailsStep.tsx
import React, { useState, useEffect } from 'react';
import { api } from '../../config/api.ts';
import { University, Course, Department } from '../../types/index.ts';
import { BookingData } from './BookingWizard.tsx';
import { ModernButton } from '../ui/ModernButton.tsx';
import { ModernInput } from '../ui/ModernInput.tsx';
import { ModernCard } from '../ui/ModernCard.tsx';
import { FileUpload } from '../FileUpload.tsx';
import { AutocompleteInput } from '../ui/AutocompleteInput.tsx';
import { ChevronLeftIcon, SearchIcon } from '../icons/index.tsx';
import { useDebounce } from '../../hooks/useDebounce.ts';

interface BookingDetailsStepProps {
  bookingData: BookingData;
  onDetailsComplete: (details: any) => void;
  onBack: () => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
}

interface UploadedFile {
  id: number;
  name: string;
  size: number;
  type: string;
  uploaded_at: string;
}

export const BookingDetailsStep: React.FC<BookingDetailsStepProps> = ({
  bookingData,
  onDetailsComplete,
  onBack,
  setError,
  setLoading
}) => {
  // Form state
  const [university, setUniversity] = useState(bookingData.university);
  const [courseNotListed, setCourseNotListed] = useState(bookingData.courseNotListed);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [manualCourseName, setManualCourseName] = useState(bookingData.manualCourseName);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [notes, setNotes] = useState(bookingData.notes);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(bookingData.uploadedFiles);
  
  // NEW: Exam type and alternative phone
  const [examType, setExamType] = useState(bookingData.examType);
  const [useAlternativePhone, setUseAlternativePhone] = useState(bookingData.useAlternativePhone);
  const [alternativePhone, setAlternativePhone] = useState(bookingData.alternativePhone);

  // Data state
  const [universities, setUniversities] = useState<University[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [searchingCourses, setSearchingCourses] = useState(false);

  const debouncedCourseSearch = useDebounce(courseSearch, 300);
  const UNIVERSITIES_WITH_COURSES = ['AUM', 'IUK','KCST'];

  // Helper function to get exam types based on university
  const getExamTypes = (uni: string) => {
    switch (uni) {
      case 'AUM':
        return ['GCA1', 'GCA2', 'Midterm', 'Final', 'Lab','Quiz'];
      case 'IUK':
        return ['Quiz 1', 'Quiz 2', 'Quiz 3', 'Quiz 4', 'Quiz 5', 'Quiz 6', 'Midterm', 'Final'];
      default:
        return [];
    }
  };

  // Helper function to validate Kuwait phone number
  const validateKuwaitPhone = (phone: string) => {
    // Remove spaces and any non-digit characters except +
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    
    // Check if it starts with +965 or just the number (any 8 digits)
    const kuwaitRegex = /^(\+965)?\d{8}$/;
    return kuwaitRegex.test(cleanPhone);
  };

  // Load initial data
  useEffect(() => {
    loadUniversities();
    loadDepartments();
  }, []);

  const selectedUniversity = universities.find(u => u.name === university);
  const isUniversityInDatabase = Boolean(selectedUniversity);
  const hasCoursesInDatabase = UNIVERSITIES_WITH_COURSES.includes(university);

  // Search courses when university and search term change
  useEffect(() => {
    if (selectedUniversity && debouncedCourseSearch.length > 0) {
      searchCourses(selectedUniversity.university_id, debouncedCourseSearch);
    } else if (selectedUniversity && debouncedCourseSearch.length === 0) {
      searchCourses(selectedUniversity.university_id, '');
    }
  }, [selectedUniversity, debouncedCourseSearch]);

  useEffect(() => {
    if (university && (!isUniversityInDatabase || !hasCoursesInDatabase)) {
      setCourseNotListed(true);
      setSelectedCourse(null);
      setCourseSearch('');
    } else if (university && hasCoursesInDatabase) {
      setCourseNotListed(false);
      setManualCourseName('');
      setSelectedDepartment(null);
    }
    
    // Reset exam type when university changes
    setExamType('');
  }, [university, isUniversityInDatabase, hasCoursesInDatabase]);

  const loadUniversities = async () => {
    try {
      const response = await api.get('/public/booking/universities');
      if (response.data.success) {
        setUniversities(response.data.data);
      }
    } catch (err) {
      setError('فشل في تحميل الجامعات');
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await api.get('/public/booking/departments');
      if (response.data.success) {
        setDepartments(response.data.data);
        
        // Restore selected department if exists
        if (bookingData.departmentId) {
          const dept = response.data.data.find(d => d.department_id === bookingData.departmentId);
          if (dept) setSelectedDepartment(dept);
        }
      }
    } catch (err) {
      setError('فشل في تحميل الأقسام');
    }
  };

  const searchCourses = async (universityId: number, query: string = '') => {
    try {
      setSearchingCourses(true);
      const response = await api.get(`/public/booking/courses/search?university_id=${universityId}&query=${query}&limit=20`);
      if (response.data.success) {
        setCourses(response.data.data.courses);
      }
    } catch (err) {
      setError('فشل في البحث عن المواد');
    } finally {
      setSearchingCourses(false);
    }
  };

  // Helper function to format course display name
  const formatCourseDisplayName = (course: any) => {
    if (course.real_name && course.name && course.real_name !== course.name) {
      return `${course.real_name} (${course.name})`;
    }
    return course.real_name || course.name;
  };

  // Helper function to get the formatted display name for selected course
  const getSelectedCourseDisplayName = () => {
    if (!selectedCourse) return '';
    return formatCourseDisplayName(selectedCourse);
  };

  const handleUniversitySelect = (uni: University) => {
    setUniversity(uni.name);
    setSelectedCourse(null);
    setCourseSearch('');
    setCourses([]);
    setExamType(''); // Reset exam type when university changes
  };

  const handleCourseSelect = (course: Course) => {
    setSelectedCourse(course);
    setCourseSearch('');
    setCourseNotListed(false);
  };

  const handleFilesChange = (files: UploadedFile[]) => {
    setUploadedFiles(files);
  };

  const canProceed = () => {
    // University is required
    if (!university.trim()) return false;
    
    // Exam type is required for AUM and IUK
    if (['AUM', 'IUK'].includes(university) && !examType.trim()) return false;
    
    // Course selection is required
    if (courseNotListed) {
      if (!manualCourseName.trim()) return false;
      if (!selectedDepartment) return false;
    } else {
      if (!selectedCourse) return false;
    }
    
    // Alternative phone validation
    if (useAlternativePhone) {
      if (!alternativePhone.trim()) return false;
      if (!validateKuwaitPhone(alternativePhone)) return false;
    }
    
    // Files are required
    if (uploadedFiles.length === 0) return false;
    
    return true;
  };
  const getValidationMessage = () => {
  const missing = [];
  
  if (!university.trim()) {
    missing.push('الجامعة');
  }
  
  if (['AUM', 'IUK'].includes(university) && !examType.trim()) {
    missing.push('نوع الامتحان');
  }
  
  if (courseNotListed) {
    if (!manualCourseName.trim()) {
      missing.push('اسم المادة');
    }
    if (!selectedDepartment) {
      missing.push('التخصص');
    }
  } else {
    if (!selectedCourse) {
      missing.push('اختيار المادة');
    }
  }
  
  if (useAlternativePhone) {
    if (!alternativePhone.trim()) {
      missing.push('رقم الهاتف البديل');
    } else if (!validateKuwaitPhone(alternativePhone)) {
      missing.push('رقم هاتف صحيح');
    }
  }
  
  if (uploadedFiles.length === 0) {
    missing.push('ملفات المادة');
  }
  
  return missing;
};
  const handleSubmit = () => {
    if (!canProceed()) return;
    
    const details = {
      university,
      courseNotListed,
      courseId: selectedCourse?.course_id || null,
      manualCourseName: courseNotListed ? manualCourseName : '',
      departmentId: courseNotListed ? selectedDepartment?.department_id : selectedCourse?.department_id,
      notes,
      examType, // NEW
      useAlternativePhone, // NEW
      alternativePhone: useAlternativePhone ? alternativePhone : '', // NEW
      uploadedFiles
    };
    
    onDetailsComplete(details);
  };

  const formatSelectedDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-JO', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    if (!time) return 'غير محدد';
    
    try {
      const [hours, minutes] = time.split(':').map(Number);
      
      // Handle invalid time values
      if (isNaN(hours) || isNaN(minutes)) {
        return time; // Return original if can't parse
      }
      
      const period = hours >= 12 ? 'م' : 'ص';
      const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      return time || 'غير محدد';
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">تفاصيل الحجز</h2>
          <p className="text-gray-600">أكمل معلومات الحجز</p>
        </div>
        <ModernButton 
          variant="outline" 
          size="sm"
          onClick={onBack}
          icon={<ChevronLeftIcon />}
        >
          رجوع
        </ModernButton>
      </div>

      {/* Booking Summary */}
      <ModernCard className="p-4 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-3">ملخص الجلسة</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center">
            <span className="text-blue-700">📅</span>
            <span className="mr-2 text-blue-800">
              <strong>التاريخ:</strong> {formatSelectedDate(bookingData.selectedDate)}
            </span>
          </div>
          <div className="flex items-center">
            <span className="text-blue-700">🕐</span>
            <span className="mr-2 text-blue-800">
              <strong>الوقت:</strong> {bookingData.selectedTime ? formatTime(bookingData.selectedTime) : 'غير محدد'}
            </span>
          </div>
        </div>
      </ModernCard>

      {/* University Selection */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <span className="ml-2">🏛️</span>معلومات الجامعة والمادة
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              الجامعة <span className="text-red-500">*</span>
            </label>
            <select
              value={university}
              onChange={(e) => setUniversity(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              required
            >
              <option value="">اختر الجامعة</option>
              <option value="AUM">الجامعة الأمريكية في الشرق الأوسط (AUM)</option>
              <option value="IUK">الجامعة الدولية في الكويت (IUK)</option>
              <option value="KCST">كلية الكويت للعلوم والتكنولوجيا (KCST)</option>
              <option value="KU">جامعة الكويت (KU)</option>
              <option value="GUST">جامعة الخليج للعلوم والتكنولوجيا (GUST)</option>
              <option value="ACK">الكلية الأسترالية في الكويت (ACK)</option>
              <option value="AUK">الجامعة الأمريكية في الكويت (AUK)</option>
              <option value="AIU">الجامعة الأمريكية الدولية - الكويت (AIU)</option>
              <option value="AOU">الجامعة العربية المفتوحة (AOU)</option>
              <option value="KILAW">كلية القانون الكويتية العالمية (KILAW)</option>
              <option value="AASU">جامعة عبدالله السالم (AASU)</option>
              <option value="CBK">كلية بوكسهل الكويت (CBK)</option>
              <option value="other">جامعة أخرى</option>
            </select>
          </div>

          {/* Exam Type Selection - NEW */}
          {university && getExamTypes(university).length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                نوع الامتحان <span className="text-red-500">*</span>
              </label>
              <select
                value={examType}
                onChange={(e) => setExamType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                required
              >
                <option value="">اختر نوع الامتحان</option>
                {getExamTypes(university).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          )}

          {/* Course Selection */}
          {university && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  المادة <span className="text-red-500">*</span>
                </label>
                
                {!courseNotListed ? (
                  <div className="space-y-3">
                    {!selectedCourse ? (
                      <AutocompleteInput
                        label=""
                        placeholder="اكتب اسم أو رمز المادة للبحث..."
                        value={courseSearch}
                        onChange={setCourseSearch}
                        onSelect={handleCourseSelect}
                        options={courses.map(course => ({
                          ...course,
                          display_name: formatCourseDisplayName(course),
                          subtitle: course.department_name
                        }))}
                        loading={searchingCourses}
                        displayKey="display_name"
                        icon={<SearchIcon />}
                      />
                    ) : (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-green-900">{getSelectedCourseDisplayName()}</span>
                            <p className="text-sm text-green-700">{selectedCourse.department_name}</p>
                          </div>
                          <ModernButton
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedCourse(null)}
                          >
                            تغيير
                          </ModernButton>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <ModernInput
                    label=""
                    value={manualCourseName}
                    onChange={(e) => setManualCourseName(e.target.value)}
                    placeholder="أدخل اسم المادة (مثال: MATH 101 - حساب التفاضل والتكامل)"
                    required
                  />
                )}
              </div>

              {/* Info message for universities not in database */}
              {!hasCoursesInDatabase && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <div className="text-blue-600 ml-2 mt-0.5">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-blue-800 text-sm">
                      يرجى إدخال اسم المادة يدوياً واختيار التخصص المناسب
                    </p>
                  </div>
                </div>
              )}

              {/* Department Selection for Manual Courses */}
              {courseNotListed && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    التخصص (القسم) <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedDepartment?.department_id || ''}
                    onChange={(e) => {
                      const dept = departments.find(d => d.department_id === parseInt(e.target.value));
                      setSelectedDepartment(dept || null);
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  >
                    <option value="">اختر التخصص</option>
                    {departments.map(dept => (
                      <option key={dept.department_id} value={dept.department_id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      </ModernCard>

      {/* Alternative Contact - NEW */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
         <span className="ml-2">📞</span>معلومات التواصل
       </h3>
       
       <div className="space-y-4">
         <div className="flex items-start">
           <input
             type="checkbox"
             id="useAlternativePhone"
             checked={useAlternativePhone}
             onChange={(e) => {
               setUseAlternativePhone(e.target.checked);
               if (!e.target.checked) {
                 setAlternativePhone('');
               }
             }}
             className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
           />
           <label htmlFor="useAlternativePhone" className="mr-3 text-sm text-gray-700">
             سيتم التواصل معي على رقم مختلف عن الرقم المسجل في الحساب
           </label>
         </div>
         
         {useAlternativePhone && (
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">
               رقم الهاتف البديل <span className="text-red-500">*</span>
             </label>
             <ModernInput
               label=""
               value={alternativePhone}
               onChange={(e) => setAlternativePhone(e.target.value)}
               placeholder="+965 12345678 أو 51234567"
               required
               className={`${!validateKuwaitPhone(alternativePhone) && alternativePhone ? 'border-red-300' : ''}`}
             />
             {alternativePhone && !validateKuwaitPhone(alternativePhone) && (
               <p className="text-red-500 text-xs mt-1">
                 يرجى إدخال رقم هاتف مكون من 8 أرقام (مثال: +96512345678 أو 12345678)
               </p>
             )}
             <p className="text-gray-500 text-xs mt-1">
               يُفضل إدخال رقم مكون من 8 أرقام مع رمز البلد +965
             </p>
           </div>
         )}
       </div>
     </ModernCard>

     {/* File Upload */}
     <ModernCard className="p-6">
       <h3 className="text-lg font-semibold text-gray-900 mb-4">
         <span className="ml-2">📁</span>ملفات المادة <span className="text-red-500">*</span>
       </h3>
       
       <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
         <p className="text-yellow-800 text-sm">
           <strong>مطلوب:</strong> يرجى تحميل ملفات الجباتر أو السلايدات أو صورة من إعلان الجامعة يحتوي على أسماء المحاضرات.
         </p>
       </div>
       
       <FileUpload
         sessionId={bookingData.sessionId}
         onFilesChange={handleFilesChange}
         maxFiles={10}
         acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png"
         maxSize={500} // 500MB like original
         endpoint="/client/bookings/upload-files"
         deleteEndpoint="/client/bookings/files"
         mode="booking"
       />
       
       {uploadedFiles.length > 0 && (
         <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
           <p className="text-green-800 font-medium">
             ✓ تم رفع {uploadedFiles.length} ملف بنجاح
           </p>
         </div>
       )}
     </ModernCard>

     {/* Notes */}
     <ModernCard className="p-6">
       <h3 className="text-lg font-semibold text-gray-900 mb-4">
         <span className="ml-2">📝</span>ملاحظات إضافية
       </h3>
       
       <textarea
         value={notes}
         onChange={(e) => setNotes(e.target.value)}
         placeholder="أدخل أي ملاحظات أو تفاصيل إضافية تريد إضافتها..."
         rows={4}
         className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
       />
     </ModernCard>

     {/* Action Buttons */}
     <div className="flex gap-4">
       <ModernButton
         variant="outline"
         onClick={onBack}
         className="flex-1"
         icon={<ChevronLeftIcon />}
       >
         رجوع
       </ModernButton>
       
       <ModernButton
         variant="primary"
         onClick={handleSubmit}
         className="flex-1"
         disabled={!canProceed()}
       >
         مراجعة وتأكيد ←
       </ModernButton>
     </div>

     {/* Requirements Warning */}
    {/* Requirements Warning */}
{!canProceed() && (
  <div className="text-center">
    <div className="text-sm text-red-600">
      <p className="font-medium mb-2">يرجى إكمال المتطلبات التالية:</p>
      <ul className="space-y-1">
        {getValidationMessage().map((item, index) => (
          <li key={index}>• {item}</li>
        ))}
      </ul>
    </div>
  </div>
)}
   </div>
 );
};